import React from 'react';
import styled from 'styled-components';
import Typography, { TypographyProps } from '@mui/material/Typography';
import HelpLink from '@ui-parts/HelpLink';
import { PALETTE } from '~/assets/styles/base';

type StyledTitle = {
  $fontWeight?: string;
};

const Wrap = styled.div<StyledTitle>`
  padding: 30px 30px;
  font-weight: ${(props) => props.$fontWeight};
`;
const FlexWrap = styled.div`
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
`;

type StyledTypographyProps = {
  $hasLabel?: boolean;
  $preLine?: boolean;
};
const StyledTypography = styled(Typography)<StyledTypographyProps>`
  margin: ${(props) => (props.$hasLabel ? '5px 20px 0 0' : '0')};
  white-space: ${(props) => (props.$preLine ? 'pre-line' : 'inherit')};
`;

const NoteText = styled(Typography)`
  line-height: 1.4;
  margin: 10px 0 -16px; // NoteTextがない時は下にpaddingを広く取るため、ネガティブマージンにて相殺
  white-space: pre-line;
` as typeof Typography; // 'component'propsがTypeScriptエラー出るため。

type StyledDescProps = {
  fontSize: string;
};
const StyledDesc = styled.span<StyledDescProps>`
  font-size: ${({ fontSize }) => fontSize};
  white-space: pre-line;
  display: inline-block;
  line-height: 1.5;
  margin-top: 10px;
  & > a {
    color: ${PALETTE.text.primary};
    text-decoration: none;
  }
  & > a:visited {
    color: ${PALETTE.text.primary};
    text-decoration: none;
  }
`;

type Props = {
  titleProps: {
    titleLabelVariant?: TypographyProps['variant'];
    titleVariant?: TypographyProps['variant'];
    descVariant?: TypographyProps['variant'];
    titleLabel?: string;
    title?: string;
    dangerouslySetDesc?: string;
    desc?: string;
    note?: string;
    customModalTitle?: JSX.Element;
    warnText?: string;
    warnTop?: string;
    warnRight?: string;
    warnLeft?: string;
    warnBottom?: string;
    warnLink?: string;
    fontWeight?: string;
  };
};

const ModalHeader: React.FC<Props> = ({
  titleProps: {
    titleLabelVariant = 'bodyDefault',
    titleVariant = 'title1',
    descVariant = 'bodyDefault',
    titleLabel = '',
    customModalTitle,
    title = '',
    dangerouslySetDesc = '',
    desc = '',
    note = '',
    warnText = '',
    warnTop = '10px',
    warnRight = '10px',
    warnLeft = '',
    warnBottom = '',
    warnLink = '',
    fontWeight = '',
  },
}): JSX.Element => {
  if (customModalTitle) return customModalTitle;

  return (
    <Wrap $fontWeight={fontWeight}>
      <FlexWrap>
        {titleLabel ? (
          <StyledTypography
            variant={titleLabelVariant}
            $hasLabel
            data-testid="uiParts-ModalHeaderTitleLabel"
          >
            {titleLabel}
          </StyledTypography>
        ) : null}
        <StyledTypography variant={titleVariant} data-testid="uiParts-ModalHeaderTitle">
          {title}
        </StyledTypography>
        {warnText ? (
          <HelpLink
            warnText={warnText}
            warnTop={warnTop}
            warnRight={warnRight}
            warnLeft={warnLeft}
            warnBottom={warnBottom}
            warnLink={warnLink}
          />
        ) : null}
      </FlexWrap>
      {dangerouslySetDesc && (
        <StyledDesc
          fontSize={descVariant}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: dangerouslySetDesc,
          }}
          data-testid="uiParts-ModalHeader-dangerouslySetDesc"
        />
      )}
      {desc && (
        <StyledTypography $preLine variant={descVariant} data-testid="uiParts-ModalHeaderDesc">
          {desc}
        </StyledTypography>
      )}
      {note && (
        <NoteText variant="body3" component="h1">
          {note}
        </NoteText>
      )}
    </Wrap>
  );
};

export default ModalHeader;
